import React from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import FreeBreakfastOutlinedIcon from "@mui/icons-material/FreeBreakfastOutlined";
import { jornadaAlertsCopy } from "./jornadaAlertsCopy";

export default function VirtualOfficeJornadaAlerts({
  alerts,
  onLeave,
  onReturn,
  leaveBusy = false,
}) {
  if (!alerts?.schedule?.applies) return null;
  const copy = jornadaAlertsCopy(alerts.schedule);
  const legalClt = Boolean(alerts.schedule.legalClt);

  return (
    <>
      {alerts.approaching && !alerts.exitOpen && !alerts.breakOpen && !alerts.returnOpen ? (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          {copy.banner}
        </Alert>
      ) : null}

      <Dialog
        open={Boolean(alerts.lateOpen && alerts.justifyMode !== "late")}
        onClose={() => {}}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>{copy.lateTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {copy.lateBody}
          </Typography>
          {copy.bankLine ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {copy.bankLine}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ flexDirection: "column", gap: 1, px: 2, pb: 2 }}>
          <Button
            fullWidth
            variant="contained"
            disabled={alerts.busy}
            onClick={() => void alerts.acknowledgeLate()}
          >
            {alerts.busy ? "Registrando…" : copy.lateAck}
          </Button>
          <Button fullWidth disabled={alerts.busy} onClick={alerts.openLateJustify}>
            {copy.lateJustify}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(alerts.exitOpen && alerts.justifyMode !== "stay")}
        onClose={() => {}}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>{copy.exitTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {copy.exitBody}
          </Typography>
          <Alert severity={alerts.schedule.exitDue?.overLegalLimit ? "error" : "warning"} sx={{ borderRadius: 2 }}>
            {copy.exitLimit || copy.exitStayHint}
          </Alert>
          {copy.bankLine ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
              {copy.bankLine}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ flexDirection: "column", gap: 1, px: 2, pb: 2 }}>
          <Button
            fullWidth
            variant="contained"
            color="warning"
            startIcon={<LogoutIcon />}
            disabled={leaveBusy || alerts.busy}
            onClick={() => onLeave?.("exit")}
          >
            {copy.leaveExit || "Encerrar expediente"}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<FreeBreakfastOutlinedIcon />}
            disabled={leaveBusy || alerts.busy}
            onClick={() => onLeave?.("break")}
          >
            {copy.leaveBreak || "Sair para intervalo"}
          </Button>
          <Button fullWidth disabled={alerts.busy} onClick={alerts.openStayJustify}>
            Justificar permanência
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(alerts.breakOpen && alerts.justifyMode !== "break")}
        onClose={() => {}}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>{copy.breakTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {copy.breakBody}
          </Typography>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            {copy.breakStayHint}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ flexDirection: "column", gap: 1, px: 2, pb: 2 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<FreeBreakfastOutlinedIcon />}
            disabled={leaveBusy || alerts.busy}
            onClick={() => onLeave?.("break")}
          >
            {copy.leaveBreak || "Sair para intervalo"}
          </Button>
          <Button fullWidth disabled={alerts.busy} onClick={alerts.openBreakJustify}>
            Justificar permanência
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(alerts.returnOpen)}
        onClose={() => {}}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>{copy.breakReturnTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {copy.breakReturnBody}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: "column", gap: 1, px: 2, pb: 2 }}>
          {typeof onReturn === "function" && alerts.schedule?.present ? (
            <Button
              fullWidth
              variant="contained"
              disabled={leaveBusy || alerts.busy}
              onClick={() => onReturn()}
            >
              {copy.breakReturnAction || "Voltar do intervalo"}
            </Button>
          ) : (
            <Alert severity="info" sx={{ width: "100%", borderRadius: 2 }}>
              {copy.breakReturnHint ||
                "Ingressar de novo no escritório registra o fim do intervalo."}
            </Alert>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(alerts.justifyMode)}
        onClose={() => !alerts.busy && alerts.closeJustify()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {alerts.justifyMode === "late"
            ? copy.lateJustify
            : alerts.justifyMode === "break"
              ? copy.breakStayTitle
              : copy.stayTitle}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {alerts.justifyMode === "late"
                ? copy.lateJustifyHint
                : alerts.justifyMode === "break"
                  ? copy.breakStayBody
                  : copy.stayBody}
            </Typography>
            <TextField
              label="Motivo"
              value={alerts.justificativa}
              onChange={(e) => alerts.setJustificativa(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              autoFocus
            />
            {alerts.justifyMode === "stay" && legalClt ? (
              <FormControl>
                <RadioGroup
                  value={alerts.compensation || "hours_bank"}
                  onChange={(e) => alerts.setCompensation(e.target.value)}
                >
                  <FormControlLabel
                    value="hours_bank"
                    control={<Radio />}
                    label={copy.stayHoursBank}
                  />
                  <FormControlLabel value="paid" control={<Radio />} label={copy.stayPaid} />
                </RadioGroup>
              </FormControl>
            ) : null}
            {alerts.error ? <Alert severity="error">{alerts.error}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={alerts.busy} onClick={alerts.closeJustify}>
            Voltar
          </Button>
          <Button
            variant="contained"
            disabled={alerts.busy || String(alerts.justificativa || "").trim().length < 8}
            onClick={() =>
              void (alerts.justifyMode === "late"
                ? alerts.submitJustifyLate()
                : alerts.justifyMode === "break"
                  ? alerts.submitJustifyBreak()
                  : alerts.submitJustifyStay())
            }
          >
            {alerts.busy
              ? "Salvando…"
              : alerts.justifyMode === "late"
                ? "Enviar justificativa"
                : copy.staySubmit}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
